/**
 * @version 1.3
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://kelnik.gitbooks.io/kelnik-documentation/content/front-end/components/utils.html documentation
 */

/**
 * DEPENDENCIES
 */
import 'url-search-params-polyfill';
import {clearAllBodyScrollLocks, disableBodyScroll} from 'body-scroll-lock';
import {DESKTOP_BREAKPOINT} from './constants';

class Utils {
    static isTouchDevice() {
        return (
            Boolean(typeof window !== 'undefined' &&
                ('ontouchstart' in window ||
                    (window.DocumentTouch &&
                        typeof document !== 'undefined' &&
                        document instanceof window.DocumentTouch))) ||
            Boolean(typeof navigator !== 'undefined' &&
                (navigator.maxTouchPoints || navigator.msMaxTouchPoints))
        );
    }

    /**
     * Метод полностью очищает весь html элемент.
     * @param {Object} element - DOM-элемент, который необходимо очистить.
     */
    static clearHtml(element) {
        element.innerHTML = '';
    }

    /**
     * Метод вставляет содержимое в блок.
     * @param {Object} element - элемент в который нужно вставить.
     * @param {Object/string} content - вставляемый контент.
     */
    static insetContent(element, content) {
        if (typeof content === 'string') {
            element.insertAdjacentHTML('beforeend', content);
        } else if (typeof content === 'object') {
            element.appendChild(content);
        }
    }

    /**
     * Метод полностью удаляет элемент из DOM-дерева.
     * @param {Object} element - элемент, который необходимо удалить.
     */
    static removeElement(element) {
        // node.remove() не работает в IE11
        element.parentNode.removeChild(element);
    }


    /**
     * Метод показывает элемент.
     * @param {Node} element - элемент, который необходимо показать.
     */
    static show(element) {
        element.style.display = 'block';
    }

    /**
     * Метод скрывает элемент.
     * @param {Node} element - элемент, который необходимо скрыть.
     */
    static hide(element) {
        element.style.display = 'none';
    }

    /**
     * Узнает index элемента в родительской элемент
     * Аналог jquery.index()
     * @param {Node} element - искомый элемент
     * @return {number} - порядковый номер (индекс) в родительском элементе
     */
    static getElementIndex(element) {
        return Array.from(element.parentNode.children).indexOf(element);
    }

    /**
     * Узнает находится ли элемент во вьюпорте
     * @param {Node} element - искомый элемент
     * @param {Number} offset - дополнительный отступ
     * @return {boolean} - возвращает true, если элемент виден на экране, и false наоборот
     */
    static isInViewport(element, offset = 0) {
        const rect = element.getBoundingClientRect();

        if (rect.width === 0 && rect.height === 0) {
            return false;
        }

        const top = rect.top + offset;
        const left = rect.left + offset;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        const belowViewport = 0;

        const verticalInView = (top <= windowHeight) && ((top + rect.height) >= belowViewport);
        const horizontalInView = (left <= windowWidth) && ((left + rect.width) >= belowViewport);

        return verticalInView && horizontalInView;
    }

    /**
     * Определяет ширину экрана
     * @returns {Number} Значение ширины экрана
     */
    static getWindowWidth() {
        return window.innerWidth || document.documentElement.clientWidth;
    }

    /**
     * Определяет ширину экрана
     * @returns {Number} Значение ширины экрана
     */
    static getScreenWidth() {
        return window.screen.width;
    }

    /**
     * Определяет высоту экрана
     * @returns {Number} Значение ширины экрана
     */
    static getScreenHeight() {
        return window.screen.height;
    }

    /**
     * Определяет высоту экрана
     * @returns {Number} Значение высоты экрана
     */
    static getWindowHeight() {
        return window.innerHeight || document.documentElement.clientHeight;
    }

    /**
     * Проверяет, мобилка или нет
     * @returns {Boolean} - true - мобилка
     */
    static isMobile() {
        return Utils.getWindowWidth() < DESKTOP_BREAKPOINT;
    }

    /**
     * Фиксирует страницу для запрета прокрутки
     * @param {Node} element - кроме данного элемента у всех остальных сбросится скролл
     */
    /* eslint-disable  */
    static bodyFixed(element) {
        disableBodyScroll(element, {
            allowTouchMove: (el) => {
                while (el && el !== document.body) {
                    if (el.getAttribute('body-scroll-lock-ignore') !== null) {
                        return true;
                    }

                    el = el.parentNode;
                }
            },
            reserveScrollBarGap: true
        });
    }
    /* eslint-enable  */

    /**
     * Снимаем фиксирование страницы
     */
    static bodyStatic() {
        clearAllBodyScrollLocks();
    }


    /**
     * Устанавливает гет-параметры
     * @param {string} url - адрес скрипта/API который будем подключать
     * @param {object} callback - функция обратного вызова, если нужна
     */
    static loadScript(url, callback) {
        const script = document.createElement('script');

        // eslint-disable-next-line consistent-return
        script.onload = () => {
            if (callback) {
                return callback();
            }
        };

        script.src = url;
        document.getElementsByTagName('head')[0].appendChild(script);
    }

    static loadHandler(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.src = url;
            img.onload = (event) => {
                const src = event.currentTarget.getAttribute('src');
                const isSvg = src.indexOf('.svg');

                if (isSvg === -1) {
                    resolve({
                        width : event.currentTarget.width,
                        height: event.currentTarget.height
                    });
                } else {
                    $.get(src, (svgxml) => {
                        const viewBox = svgxml.documentElement.attributes.viewBox.value.split(' ');

                        resolve({
                            width : Number(viewBox[2]),
                            height: Number(viewBox[3])
                        });
                    }, 'xml');
                }
            };
            img.onerror = (error) => {
                reject(error);
            };
        });
    }


    /**
     * Debouncing функции означает, что все вызовы будут игнорироваться до тех пор,
     * пока они не прекратятся на определённый период времени. Только после этого функция будет вызвана.
     * @param {function} fun - Функция выполнения
     * @param {number} time - Миллисекунды
     * @return {function} - Замыкание
     */
    static debounce(fun, time) {
        let lastCall = null;

        return (args) => {
            const previousCall = lastCall;

            lastCall = Date.now();

            if (previousCall && ((lastCall - previousCall) <= time)) {
                clearTimeout(this.lastCallTimer);
            }

            this.lastCallTimer = setTimeout(() => {
                fun(args);
            }, time);
        };
    }

    /**
     * Метод проверяет наличие интернета
     * @return {boolean} - При наличии результатом будет true, а при отсутствии false.
     */
    static checkInternetConnection() {
        return navigator.onLine;
    }

    /**
     * Метод отправляет ajax запрос на сервер.
     * @param {Object} data - отправляемые данные.
     * @param {String} url - маршрут по которому нужно произвести запрос.
     * @param {Object} callback -  функции обратного вызова, при успехе вызовет поле success, а при ошибке - error.
     * @param {String} method - метод отправки запроса
     */
    static send(data, url, callback = {}, method = 'GET') {
        const xhr = new XMLHttpRequest();
        const statusSuccess = 200;

        xhr.open(method, url);

        if (!(data instanceof FormData)) {
            xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
        }

        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.send(data);

        xhr.onload = function XHR() {
            if (xhr.status === statusSuccess) {
                const req = JSON.parse(this.responseText);

                if (callback.success) {
                    callback.success(req);
                }
            } else if (callback.error) {
                callback.error(xhr.status);
            }

            if (callback.complete) {
                callback.complete();
            }
        };

        xhr.onerror = function XHR() {
            if (callback.error) {
                callback.error(xhr.status);
            }

            if (callback.complete) {
                callback.complete();
            }
        };
    }

    /**
     * Генерация псевдослучайных чисел в заданном  числовом интервале
     * @param {Number} min - минимальное псевдослучайное число
     * @param {Number} max - максимальное псевдослучайное число
     * @return {Number} - возвращает псевдослучайное число в интервале [min, max]
     */
    static random(min, max) {
        return Math.floor((Math.random() * (max - min)) + min);
    }

    /**
     * Конвертирует число или строку в строку с рарядами.
     * @param {string/number} value - число или строка.
     * @return {string} - преобразованная строка.
     */
    static convertToDigit(value) {
        return Number(value) ? Number(value).toLocaleString('ru-Ru') : value;
    }

    /**
     * Конвертирует целое число в дробное
     * @param {string/number} value - число или строка.
     * @param {number} denominator - целый делитель.
     * @return {number} - преобразованная строка.
     */
    static convertToRank(value, denominator) {
        const result = Number(value);

        return result / denominator;
    }

    /**
     * Выбирает окончание слова.
     * @param {number} n - количество
     * @param {Object} words - масcив слов. Например показать еще ['квартиру', 'квартиры', 'квартир']
     * @return {string} - слово в правильном склонении.
     */
    static pluralWord(n, words) {
        /* eslint-disable  */
        const $i = n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
        /* eslint-enable*/

        return words[$i];
    }

    /**
     * декодирование строки из base-64 в Unicode
     * @param {String} str - закодированная строка в base64
     * @returns {String} Декодированная строка
     */
    static fromBase64(str) {
        return decodeURIComponent(escape(window.atob(str)));
    }

    /**
     * кодирование строки Unicode в base-64
     * @param {String} str - Строка
     * @return {String} Закодированная строка в base64
     */
    static toBase64(str) {
        return window.btoa(unescape(encodeURIComponent(str)));
    }

    /**
     * Проверяет является ли сущность дом элементом
     * @param {*} elem - элемент для проверки
     * @returns {boolean} true - является
     */
    static isElement(elem) {
        return Boolean(elem.tagName);
    }

    /**
     * Проверяет, является ли parent родителем child
     * @param {*} child - наследник
     * @param {*} parent - родитель
     * @return {boolean} - true - является
     */
    static isDescendant(child, parent) {
        let node = child.parentNode;

        while (node !== null) {
            if (node === parent) {
                return true;
            }
            node = node.parentNode;
        }

        return false;
    }

    /**
     * Устанавиливает гет-параметры
     * @param {array} params - массив объектов (ключ/значение) для установки в гет
     */
    static setUrlParams(params) {
        const query = new URLSearchParams();

        params.forEach((item) => {
            query.append(item.param, item.value);
        });

        window.history.pushState(null, null, `?${query.toString()}`);
    }

    /**
     * Получает значение гет-параметра
     * @param {string} param - ключ для поиска в гет
     * @return {string} value - значение гет
     */
    static getUrlParams(param) {
        const get = window.location.search;
        const url = new URLSearchParams(get);

        return url.get(param);
    }

    /**
     * Метод конвертирует строку (можно с разрядами) в число
     * @param {string} str - необходимая строка.
     * @return {number} - сковертированное число.
     */
    static convertToNumber(str) {
        return parseFloat(str
            .toString()
            .replace(/\s/g, ''));
    }

    /**
     * Определяет положение скролла
     * @returns {Number} Значение положения скролла
     */
    static getScrollTop() {
        return window.pageYOffset || document.documentElement.scrollTop;
    }

    /**
     * Возвращает пустая ли переменная
     * @param {any} variable - переменная для проверки
     * @return {boolean} - пустая ли переменная?
     */
    static isSet(variable) {
        return !(typeof variable === 'undefined' || variable === null);
    }

    /**
     * Метод проверяет присутствует ли ключ в объекте
     * @param {Object} object - проверяем объект
     * @param {String} key - ключ, наличие которого проверяет в объекте
     * @return {boolean} - присутствует или нет ключ в объекте
     */
    static keyExist(object, key) {
        return Object.prototype.hasOwnProperty.call(object, key);
    }

    /**
     * Установка cookie
     * @param {string} name - имя cookie
     * @param {any} value - значение cookie
     * @param {object} options - объект опций
     */
    static setCookie(name, value, options = {}) {
        // eslint-disable-next-line no-param-reassign
        options = {
            path: '/',
            ...options
        };

        if (options.expires instanceof Date) {
            options.expires = options.expires.toUTCString();
        }

        let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        for (const optionKey in options) {
            if (Utils.keyExist(options, optionKey)) {
                updatedCookie = `${updatedCookie}; ${optionKey}`;

                const optionValue = options[optionKey];

                if (optionValue !== true) {
                    updatedCookie = `${updatedCookie}=${optionValue}`;
                }
            }
        }
        document.cookie = updatedCookie;
    }

    /**
     * Получает cookie по имени
     * @param {string} name - имя куки
     * @return {*} - данные из куки
     */
    static getCookie(name) {
        const matches = document.cookie
            .match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`));

        return matches ?
            decodeURIComponent(matches[1]) :
            // eslint-disable-next-line no-undefined
            undefined;
    }

    /**
     * Удаляет cookie по имени
     * @param {string} name - имя куки
     */
    static deleteCookie(name) {
        Utils.setCookie(name, '', {
            'max-age': -1
        });
    }
}

class Counter {
    constructor(stopValue = 3) {
        this.stopValue = stopValue;
        this.countValue = this.stopValue;
    }

    tick() {
        if (!this.countValue) {
            this.countValue = this.stopValue;

            return true;
        }

        this.countValue--;

        return false;
    }
}

class InternetConnection {
    constructor(callback = () => {
        return null;
    }, interval = 200) {
        this.counter = new Counter(3);

        this.callback = callback;
        this.interval = interval;

        this._bindMethodsContext();
    }

    connect() {
        if (!Utils.checkInternetConnection() && !this.counter.tick()) {
            this._reconnect(this.connect);

            return;
        }

        this.callback();
    }

    _bindMethodsContext() {
        this.connect = this.connect.bind(this);
    }

    /**
     * @Public
     * Возвращает контент с ошибками сети
     * @return {Object} - объект со списком контента для обработки ошибок
     */
    getErrorMessage() {
        return Utils.checkInternetConnection() ?
            '<h1>На сервере произошла ошибка. Повторите запрос позднее</h1>' :
            '<h1>Вы не подключены к интернету. Повторите запрос позднее</h1>';
    }

    _reconnect(callback) {
        this.setInterval = setTimeout(() => {
            callback();
        }, this.interval);
    }
}

export {Utils, InternetConnection};
